import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import {
	FormGroup,
	Input,
	Label
} from 'reactstrap';

import { messages } from '../../../_shared/i18n/messages';

import './LikertScaleInput.scss';

const propTypes = {

	/**
	 * The input name.
	 */
	name: PropTypes.string.isRequired,

	/**
	 * The input value.
	 */
	value: PropTypes.oneOfType([
		PropTypes.number,
		PropTypes.string,
	]),

	/**
	 * The input options (if any).
	 */
	options: PropTypes.arrayOf(PropTypes.shape({

		id: PropTypes.string.isRequired,

		value: PropTypes.oneOfType([
			PropTypes.number,
			PropTypes.string,
		]).isRequired,

		label: PropTypes.string.isRequired,

	})),

	/**
	 * True, if editing the input value.
	 */
	isEditing: PropTypes.bool,

	/**
	 * True, if the input value is invalid.
	 */
	isInvalid: PropTypes.bool,

	/**
	 * True, if the input value is disabled.
	 */
	disabled: PropTypes.bool,

	/**
	 * Callback function to handle changes to the input value.
	 */
	onChange: PropTypes.func,

};

const renderLabel = (option, options) => {

	const numWithLabels = options.filter((tempOption) => {

		const tempLikertValue = tempOption.value.toString();
		const tempLikertLabel = tempOption.label;

		return tempLikertLabel !== tempLikertValue;

	}).length;

	if (option.label) {

		// Check if all the options have a custom label
		if (numWithLabels === options.length) {
			return option.label;
		}

		// Check if the custom label is the same as the value
		if (option.label === option.value.toString()) {
			return option.value;
		}

		// Otherwise, combine the option value and the custom label
		return `${option.value} - ${option.label}`;

	}

	// Otherwise, just use the option value
	return option.value;

};

const renderValue = (parsedValue, parsedOptions) => {

	const selectedOption = parsedOptions.find((option) => option.value === parsedValue);

	// Check if the selected option has a custom label
	if (selectedOption?.label) {

		// Combine the option value and the custom label
		return `${parsedValue} - ${selectedOption.label}`;

	}

	// Otherwise, just use the option value
	return parsedValue;

};

const LikertScaleInput = (props) => {

	const {
		name,
		value,
		options,
		isEditing,
		isInvalid,
		disabled,
		onChange,
	} = props;

	// Convert the selected likert option value to integer format
	const parsedValue = parseInt(value, 10);

	let optionsToShow = options || [];

	// Convert the likert option values to integer format and sort in ASC order based on value
	optionsToShow = optionsToShow
		.map((option) => ({
			...option,
			value: parseInt(option.value, 10),
		}))
		.sort((option1, option2) => option1.value - option2.value);

	if (!isEditing) {

		return (
			<div className="tc-material-input-display tc-material-input-display-likert">
				{!isNaN(parsedValue) &&
					<FormGroup check>
						<Input
							name={`option-display-${name}`}
							type="radio"
							checked
							disabled />
						{' '}
						<Label
							for={`option-display-${name}`}
							check>
							{renderValue(parsedValue, optionsToShow)}
						</Label>
					</FormGroup>
				}
				{isNaN(parsedValue) &&
					<span className="tc-material-input-display-empty-value">
						<FormattedMessage {...messages.TC_COMPONENTS_FORM_INPUT_TEXT_EMPTY_VALUE} />
					</span>
				}
			</div>
		);

	}

	return (
		<div
			role="group"
			aria-labelledby={name}
			className={`tc-material-input-likert ${isInvalid ? 'tc-material-input-invalid' : ''}`}>
			{/* Horizontal Likert Scale for sm devices and up */}
			<table className="d-none d-sm-table">
				<tbody>
					<tr>
						{optionsToShow.map((option) => (
							<td key={option.id}>
								{renderLabel(option, optionsToShow)}
							</td>
						))}
					</tr>
					<tr>
						{optionsToShow.map((option) => (
							<td key={option.id}>
								<FormGroup check>
									<Input
										id={option.id}
										name={`${name}-desktop`}
										type="radio"
										value={option.value}
										checked={!isNaN(parsedValue) && option.value === parsedValue}
										invalid={isInvalid}
										disabled={disabled}
										onChange={(event) => onChange(event.target.value)} />
									{' '}
									<Label
										className="sr-only"
										for={option.id}
										check>
										{renderLabel(option, optionsToShow)}
									</Label>
								</FormGroup>
							</td>
						))}
					</tr>
				</tbody>
			</table>
			{/* Default to vertically stacked Radio inputs on xs devices */}
			<div className="d-block d-sm-none">
				{optionsToShow.map((option) => (
					<FormGroup
						key={option.id}
						check>
						<Input
							id={option.id}
							type="radio"
							name={`${name}-mobile`}
							value={option.value}
							checked={!isNaN(parsedValue) && option.value === parsedValue}
							invalid={isInvalid}
							disabled={disabled}
							onChange={(event) => onChange(event.target.value)} />
						{' '}
						<Label
							for={option.id}
							check>
							{renderLabel(option, optionsToShow)}
						</Label>
					</FormGroup>
				))}
			</div>
		</div>
	);
};

LikertScaleInput.propTypes = propTypes;

export default LikertScaleInput;
